import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCalculator,
  FaUserCheck,
  FaPhone,
  FaInfoCircle
} from 'react-icons/fa';

const Calculator = () => {
  const [roles, setRoles] = useState({
    frontend: { quantity: 0, rate: 70 },
    backend: { quantity: 0, rate: 75 },
    fullstack: { quantity: 0, rate: 80 },
    mobile: { quantity: 0, rate: 70 },
    qa: { quantity: 0, rate: 45 },
    devops: { quantity: 0, rate: 85 },
    uiux: { quantity: 0, rate: 55 },
    pm: { quantity: 0, rate: 60 }
  });

  const [settings, setSettings] = useState({
    hoursPerDay: 8,
    workingDaysPerMonth: 22,
    engagementType: 'full-time', // full-time, part-time, custom
    duration: 1 // months
  });

  const [results, setResults] = useState({
    monthlyCost: 0,
    totalCost: 0,
    roleBreakdown: []
  });

  const roleLabels = {
    frontend: 'Frontend Engineer',
    backend: 'Backend Engineer',
    fullstack: 'Full Stack Engineer',
    mobile: 'Mobile Engineer',
    qa: 'QA Engineer',
    devops: 'DevOps Engineer',
    uiux: 'UI/UX Designer',
    pm: 'Project Manager'
  };

  const calculateCost = useCallback(() => {
    let monthlyCost = 0;
    const roleBreakdown = [];

    Object.entries(roles).forEach(([role, data]) => {
      if (data.quantity > 0) {
        const roleMonthlyCost = data.rate * data.quantity * settings.hoursPerDay * settings.workingDaysPerMonth;
        monthlyCost += roleMonthlyCost;
        roleBreakdown.push({
          role: roleLabels[role],
          quantity: data.quantity,
          rate: data.rate,
          cost: roleMonthlyCost
        });
      }
    });

    const totalCost = monthlyCost * settings.duration;

    setResults({
      monthlyCost,
      totalCost,
      roleBreakdown
    });
  }, [roles, settings]);

  useEffect(() => {
    calculateCost();
  }, [calculateCost]);

  const updateRoleQuantity = (role, quantity) => {
    setRoles(prev => ({
      ...prev,
      [role]: { ...prev[role], quantity: Math.max(0, Math.min(10, quantity)) }
    }));
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section" style={{ paddingTop: '120px', backgroundColor: 'var(--bg-grey-light)' }}>
        <div className="container text-center">
          <FaCalculator style={{ fontSize: '48px', color: 'var(--primary-green)', marginBottom: '16px' }} />
          <h1 className="h1">Team Cost Calculator</h1>
          <p className="text-lg mb-8" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            Estimate your monthly staff augmentation budget with complete transparency.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="section">
        <div className="container">
          <div className="grid grid-2" style={{ gap: '48px' }}>
            {/* Input Panel */}
            <div>
              <h2 className="h3 mb-6">Select Engineering Roles</h2>

              {Object.entries(roles).map(([role, data]) => (
                <div key={role} className="card mb-4" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 className="h4" style={{ margin: '0 0 4px 0' }}>{roleLabels[role]}</h4>
                      <span style={{ color: 'var(--primary-green)', fontWeight: '600' }}>${data.rate}/hr</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        onClick={() => updateRoleQuantity(role, data.quantity - 1)}
                        style={{
                          width: '32px',
                          height: '32px',
                          border: '1px solid var(--border-grey)',
                          background: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >-</button>
                      <span style={{ minWidth: '40px', textAlign: 'center', fontWeight: '600' }}>{data.quantity}</span>
                      <button
                        onClick={() => updateRoleQuantity(role, data.quantity + 1)}
                        style={{
                          width: '32px',
                          height: '32px',
                          border: '1px solid var(--border-grey)',
                          background: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >+</button>
                    </div>
                  </div>
                </div>
              ))}

              <h2 className="h3 mb-6">Work Settings</h2>

              <div className="card mb-4" style={{ padding: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Hours per day: {settings.hoursPerDay}
                </label>
                <input
                  type="range"
                  min="4"
                  max="12"
                  value={settings.hoursPerDay}
                  onChange={(e) => updateSetting('hoursPerDay', parseInt(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="card mb-4" style={{ padding: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Working days per month: {settings.workingDaysPerMonth}
                </label>
                <input
                  type="range"
                  min="15"
                  max="30"
                  value={settings.workingDaysPerMonth}
                  onChange={(e) => updateSetting('workingDaysPerMonth', parseInt(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="card mb-4" style={{ padding: '20px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
                  Engagement Type
                </label>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {['full-time', 'part-time', 'custom'].map(type => (
                    <label key={type} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="engagementType"
                        value={type}
                        checked={settings.engagementType === type}
                        onChange={(e) => updateSetting('engagementType', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                    </label>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding: '20px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
                  Duration
                </label>
                <select
                  value={settings.duration}
                  onChange={(e) => updateSetting('duration', parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-grey)',
                    borderRadius: 'var(--border-radius)',
                    fontSize: '16px'
                  }}
                >
                  <option value={0.08}>2 weeks</option>
                  <option value={1}>1 month</option>
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                </select>
              </div>
            </div>

            {/* Results Panel */}
            <div>
              <h2 className="h3 mb-6">Cost Breakdown</h2>

              <div className="card" style={{ padding: '32px', marginBottom: '24px' }}>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                  <div style={{ fontSize: '48px', fontWeight: '700', color: 'var(--primary-green)', marginBottom: '8px' }}>
                    ${results.monthlyCost.toLocaleString()}
                  </div>
                  <div style={{ color: 'var(--text-medium)' }}>Monthly Cost</div>
                </div>

                {settings.duration > 1 && (
                  <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{ fontSize: '36px', fontWeight: '700', color: 'var(--primary-green)', marginBottom: '8px' }}>
                      ${results.totalCost.toLocaleString()}
                    </div>
                    <div style={{ color: 'var(--text-medium)' }}>Total Cost ({settings.duration} months)</div>
                  </div>
                )}

                {results.roleBreakdown.length > 0 && (
                  <div>
                    <h4 className="h4 mb-4">Role-wise Breakdown</h4>
                    {results.roleBreakdown.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: index < results.roleBreakdown.length - 1 ? '1px solid var(--border-grey)' : 'none'
                      }}>
                        <span>{item.role} ({item.quantity})</span>
                        <span style={{ fontWeight: '600' }}>${item.cost.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ fontSize: '14px', color: 'var(--text-medium)', fontStyle: 'italic', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaInfoCircle /> * This estimate is indicative. Final pricing depends on seniority and interview outcomes.
              </div>

              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                <Link to="/contact" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <FaUserCheck /> Get Profiles in 24--72 Hours
                </Link>
                <Link to="/contact" className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <FaPhone /> Schedule a Call
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calculator;
